import { useState, useEffect, useRef } from 'react';
import { authApi } from '../api/client';

function CallbackHandler() {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authentication...');
  const [debugInfo, setDebugInfo] = useState(null);
  const hasRun = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode (development)
    if (hasRun.current) return;
    hasRun.current = true;
    
    handleCallback();
  }, []);

  const handleCallback = async () => {
    // Extract code from URL query parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    console.log('🔍 Callback URL params:', { code: code?.substring(0, 20) + '...', error, errorDescription });

    // Check for Auth0 errors first (before any async operations)
    if (error) {
      // For access_denied errors, redirect to dedicated page to prevent loop
      if (error === 'access_denied') {
        window.location.href = `/access-denied?message=${encodeURIComponent(errorDescription || 'Access denied')}`;
        return;
      }
      
      // For other Auth0 errors, show error screen
      setStatus('error');
      setMessage(`Auth0 error: ${errorDescription || error}`);
      setDebugInfo({ error, errorDescription });
      
      // Auto-logout after 8 seconds to clear session and allow retry
      setTimeout(() => {
        window.location.href = '/logout';
      }, 8000);
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No authorization code received from Auth0');
      setDebugInfo({ urlSearch: window.location.search });
      return;
    }

    // If we have a valid code, proceed with exchange
    try {
      console.log('📤 Sending code to /auth/callback...');
      setMessage('Exchanging authorization code for session...');

      // Exchange code for session
      const { response, data } = await authApi.callback(code);

      console.log('📥 Response status:', response.status);

      if (response.ok) {
        console.log('✅ Success:', data);
        setStatus('success');
        setMessage('Authentication successful! Redirecting...');
        
        // Shorter delay - redirect quickly on success
        setTimeout(() => {
          window.location.href = '/';
        }, 800);
      } else {
        console.error('❌ Auth failed:', data);
        setStatus('error');
        setMessage(`Authentication failed: ${data.detail || 'Unknown error'}`);
        setDebugInfo(data);
        
        // Auto-logout after 8 seconds to clear session
        setTimeout(() => {
          window.location.href = '/logout';
        }, 8000);
      }
    } catch (err) {
      console.error('💥 Callback handling error:', err);
      setStatus('error');
      setMessage(`Unexpected error: ${err.message}`);
      setDebugInfo({ error: err.toString() });
      
      // Auto-logout after 8 seconds
      setTimeout(() => {
        window.location.href = '/logout';
      }, 8000);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      textAlign: 'center'
    }}>
      {status === 'processing' && (
        <>
          <img 
            src="/lendport_logo_icon_text.svg" 
            alt="Lendport" 
            style={{ 
              width: '160px', 
              opacity: 0.3,
              filter: 'grayscale(100%)',
              marginBottom: '30px'
            }} 
          />
          <div style={{
            width: '180px',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)',
            marginBottom: '25px'
          }}></div>
          <h2 style={{ color: '#6b7280', fontWeight: '400', fontSize: '18px' }}>Authenticating</h2>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>{message}</p>
        </>
      )}
      {status === 'success' && (
        <>
          <img 
            src="/lendport_logo_icon_text.svg" 
            alt="Lendport" 
            style={{ 
              width: '160px', 
              opacity: 0.5,
              filter: 'grayscale(100%)',
              marginBottom: '30px'
            }} 
          />
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: '#f0fdf4',
            border: '2px solid #10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            <div style={{
              width: '0',
              height: '0',
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderBottom: '14px solid #10b981',
              transform: 'rotate(45deg) translateX(3px)'
            }}></div>
          </div>
          <h2 style={{ color: '#10b981', fontWeight: '500', fontSize: '18px' }}>Authenticated</h2>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>{message}</p>
        </>
      )}
      {status === 'error' && (
        <>
          <img 
            src="/lendport_logo_icon_text.svg" 
            alt="Lendport" 
            style={{ 
              width: '160px', 
              opacity: 0.3,
              filter: 'grayscale(100%)',
              marginBottom: '30px'
            }} 
          />
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: '#fef2f2',
            border: '2px solid #ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              width: '24px',
              height: '2px',
              background: '#ef4444',
              transform: 'rotate(45deg)'
            }}></div>
            <div style={{
              position: 'absolute',
              width: '24px',
              height: '2px',
              background: '#ef4444',
              transform: 'rotate(-45deg)'
            }}></div>
          </div>
          <h2 style={{ color: '#ef4444', fontWeight: '500', fontSize: '18px', marginBottom: '12px' }}>Authentication Failed</h2>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '1rem', maxWidth: '500px' }}>{message}</p>
          {debugInfo && (
            <div style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem',
              maxWidth: '600px',
              textAlign: 'left'
            }}>
              <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                Debug Information:
              </div>
              <pre style={{ 
                fontSize: '11px', 
                color: '#6b7280', 
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
          <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '1rem' }}>
            Clearing session in 8 seconds to allow retry...
          </p>
          <button
            onClick={() => window.location.href = '/logout'}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Try Different Account
          </button>
        </>
      )}
    </div>
  );
}

export default CallbackHandler;
